import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { tones, toneForStatus, hexToRgba } from '@/lib/theme';

/* ------------------------------------------------------------------ */
/* GlassCard                                                           */
/* ------------------------------------------------------------------ */
export function GlassCard({ title, icon, action, children, className = '', bodyClassName = '', hoverable = false, style, ...rest }) {
  return (
    <div className={`glass-card ${hoverable ? 'hoverable' : ''} ${className}`} style={style} {...rest}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[var(--aampl-border)] flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            {title && <span className="section-title-bar" />}
            {icon}
            {title && <h3 className="card-heading truncate">{title}</h3>}
          </div>
          {action && <div className="flex items-center gap-2 shrink-0">{action}</div>}
        </div>
      )}
      <div className={`p-5 ${bodyClassName}`}>{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* IconChip                                                            */
/* ------------------------------------------------------------------ */
export function IconChip({ tone = 'indigo', icon: Icon, size = 44, iconSize = 20, className = '' }) {
  const t = tones[tone] || tones.indigo;
  return (
    <span
      className={`icon-chip ${className}`}
      style={{ width: size, height: size, background: t.soft, color: t.accent, boxShadow: `inset 0 0 0 1px ${hexToRgba(t.accent, 0.12)}` }}
    >
      {Icon && <Icon size={iconSize} strokeWidth={2} />}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* StatusBadge                                                         */
/* ------------------------------------------------------------------ */
export function StatusBadge({ status, label, tone, className = '', size = 'sm' }) {
  const key = tone || toneForStatus(status);
  const t = tones[key] || tones.slate;
  const pad = size === 'md' ? '6px 14px' : '4px 11px';
  const fs = size === 'md' ? 12 : 11;
  return (
    <span className={`section-badge ${className}`} style={{ background: t.soft, color: t.deep, borderColor: t.ring, padding: pad, fontSize: fs }}>
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: t.accent, boxShadow: `0 0 0 3px ${hexToRgba(t.accent, 0.18)}` }} />
      {label ?? status}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Sparkline                                                           */
/* ------------------------------------------------------------------ */
export function Sparkline({ data = [], color = '#4F6BFF', width = 96, height = 30, id }) {
  const pts = (data || []).filter((v) => typeof v === 'number' && !Number.isNaN(v));
  const uid = React.useId();
  const gid = `spark-${id || uid}`.replace(/[^a-zA-Z0-9_-]/g, '');
  if (pts.length === 0) {
    return (
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
        <line x1="2" y1={height / 2} x2={width - 2} y2={height / 2} stroke={color} strokeOpacity="0.25" strokeWidth="2" strokeDasharray="3 4" strokeLinecap="round" />
      </svg>
    );
  }
  const series = pts.length === 1 ? [pts[0], pts[0]] : pts;
  const min = Math.min(...series), max = Math.max(...series);
  const range = max - min || 1;
  const px = 3, py = 4;
  const step = (width - px * 2) / (series.length - 1);
  const coords = series.map((v, i) => [px + i * step, height - py - ((v - min) / range) * (height - py * 2)]);
  const d = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${d} L${coords[coords.length - 1][0].toFixed(1)},${height} L${coords[0][0].toFixed(1)},${height} Z`;
  const [lx, ly] = coords[coords.length - 1];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lx} cy={ly} r="2.8" fill="#fff" stroke={color} strokeWidth="2" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* KpiCard                                                             */
/* ------------------------------------------------------------------ */
export function KpiCard({ label, value, target, tone = 'indigo', icon, spark, active = false, onClick, testId }) {
  const t = tones[tone] || tones.indigo;
  return (
    <button
      type="button"
      className={`kpi-glass ${active ? 'active' : ''}`}
      style={{
        background: active ? t.fill : t.rest,
        outlineColor: active ? t.ring : undefined,
        boxShadow: active ? `0 12px 35px ${hexToRgba(t.accent, 0.22)}, 0 0 0 1px ${t.ring}` : undefined,
      }}
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
    >
      <span className="kpi-glow" style={{ background: t.accent }} />
      <div className="flex items-start justify-between gap-3">
        <IconChip tone={tone} icon={icon} />
        <span className="w-2 h-2 rounded-full mt-1" style={{ background: t.accent, boxShadow: `0 0 0 4px ${hexToRgba(t.accent, 0.16)}` }} />
      </div>
      <div className="kpi-label mt-4">{label}</div>
      <div className="kpi-foot">
        <div>
          <div className="kpi-value">{value}</div>
          {target && <div className="kpi-target">Target: {target}</div>}
        </div>
        <div className="shrink-0 pb-1 ml-auto"><Sparkline data={spark} color={t.accent} width={84} height={28} /></div>
      </div>
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* SectionStatusCard                                                   */
/* ------------------------------------------------------------------ */
export function SectionStatusCard({ label, status, statusLabel, tone = 'indigo', icon, onClick, testId }) {
  return (
    <button type="button" className="section-card" onClick={onClick} data-testid={testId}>
      <IconChip tone={tone} icon={icon} size={46} iconSize={21} />
      <div className="flex-1 min-w-0">
        <div className="text-[14px] font-semibold text-[var(--aampl-text)] leading-tight">{label}</div>
        <div className="mt-1.5"><StatusBadge status={status} label={statusLabel} /></div>
      </div>
      <ChevronRight size={18} className="section-card-arrow" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* GradientButton                                                      */
/* ------------------------------------------------------------------ */
export function GradientButton({ children, icon: Icon, className = '', ...rest }) {
  return (
    <button type="button" className={`gradient-btn ${className}`} {...rest}>
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

export function OutlineButton({ children, icon: Icon, className = '', ...rest }) {
  return (
    <button type="button" className={`outline-btn ${className}`} {...rest}>
      {Icon && <Icon size={16} />}
      {children}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* HeaderSelector                                                      */
/* ------------------------------------------------------------------ */
export function HeaderSelector({ icon: Icon, label, items, renderItem, selectedId, onSelect, emptyText = 'Nothing available', testId, itemTestId, width = 'w-56' }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="header-selector" onClick={() => setOpen((o) => !o)} data-testid={testId} aria-haspopup="listbox" aria-expanded={open}>
        {Icon && <Icon size={15} />}
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className={`chev transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`header-menu ${width} max-h-80 overflow-y-auto`} role="listbox">
          {items.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">{emptyText}</div>}
          {items.map((it) => (
            <div
              key={it.id}
              role="option"
              aria-selected={selectedId === it.id}
              className={`header-menu-item ${selectedId === it.id ? 'selected' : ''}`}
              onClick={() => { onSelect(it); setOpen(false); }}
              data-testid={itemTestId ? itemTestId(it) : undefined}
            >
              {renderItem(it)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PageHeader                                                          */
/* ------------------------------------------------------------------ */
export function PageHeader({ title, subtitle, sourceBadge, actions, eyebrow }) {
  return (
    <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
      <div className="min-w-0">
        {eyebrow && <div className="eyebrow mb-1">{eyebrow}</div>}
        <h2 className="page-title">{title}</h2>
        {subtitle && <div className="page-subtitle">{subtitle}</div>}
        {sourceBadge && (
          <div className="inline-flex items-center gap-1.5 mt-3 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full" style={{ background: tones.indigo.soft, color: tones.indigo.deep, border: `1px solid ${tones.indigo.ring}` }}>
            {sourceBadge}
          </div>
        )}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* SidebarItem                                                         */
/* ------------------------------------------------------------------ */
export function SidebarItem({ icon: Icon, label, active, indent, tone = 'indigo', onClick, testId }) {
  const t = tones[tone] || tones.indigo;
  const style = active
    ? { background: t.fill, color: t.deep, borderColor: t.ring, boxShadow: `0 8px 22px ${hexToRgba(t.accent, 0.18)}` }
    : { background: t.rest, color: '#334155', borderColor: hexToRgba(t.accent, 0.10) };
  return (
    <div
      className={`sidebar-link ${active ? 'active' : ''} ${indent ? 'indent' : ''}`}
      style={{ ...style, '--nav-accent': t.accent, '--nav-soft': t.soft, '--nav-ring': t.ring }}
      onClick={onClick}
      data-testid={testId}
      role="link"
      tabIndex={0}
      aria-current={active ? 'page' : undefined}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
    >
      <span className="sidebar-chip" style={{ background: active ? '#fff' : t.soft, color: t.accent }}>
        <Icon size={indent ? 14 : 16} strokeWidth={2} />
      </span>
      <span className="truncate">{label}</span>
      <span className="sidebar-dot" style={{ background: t.accent }} />
    </div>
  );
}
